/**
 * The short code a connector shows when it asks to be paired (RFC 8628 §6.1;
 * `server/src/dmw/accounts/domain/connectors.py`, `UserCode`): eight letters from an
 * alphabet without vowels and look-alikes, shown as `XXXX-XXXX`. Typed in any case, with
 * or without the hyphen and spaces, as the server accepts it.
 */

export const USER_CODE_ALPHABET = "BCDFGHJKLMNPQRSTVWXZ";
export const USER_CODE_LENGTH = 8;
const USER_CODE_GROUP_LENGTH = 4;
/** The first character (a whole code point) that is not in the alphabet. */
const OUTSIDE_ALPHABET = new RegExp(`[^${USER_CODE_ALPHABET}]`, "u");

declare const userCodeBrand: unique symbol;

/** A well-formed user code in its display form `XXXX-XXXX`. Made only by `parseUserCode`. */
export type UserCode = string & { readonly [userCodeBrand]: true };

export type UserCodeParseResult =
  | { readonly kind: "valid"; readonly userCode: UserCode }
  /** Nothing typed yet. */
  | { readonly kind: "empty" }
  /** Only allowed letters so far, but too few or too many. */
  | { readonly kind: "wrongLength"; readonly letterCount: number }
  /** A character that never appears in a user code, such as a vowel or a digit. */
  | { readonly kind: "invalidCharacter"; readonly character: string };

/** Upper case, without hyphens and white space: the letters the server compares. */
export function normalizeUserCodeInput(text: string): string {
  return text.toUpperCase().replace(/[\s-]/g, "");
}

export function parseUserCode(text: string): UserCodeParseResult {
  const letters = normalizeUserCodeInput(text);
  if (letters === "") {
    return { kind: "empty" };
  }
  const invalidCharacter = OUTSIDE_ALPHABET.exec(letters);
  if (invalidCharacter !== null) {
    return { kind: "invalidCharacter", character: invalidCharacter[0] };
  }
  if (letters.length !== USER_CODE_LENGTH) {
    return { kind: "wrongLength", letterCount: letters.length };
  }
  return {
    kind: "valid",
    userCode: `${letters.slice(0, USER_CODE_GROUP_LENGTH)}-${letters.slice(USER_CODE_GROUP_LENGTH)}` as UserCode,
  };
}
